/**
 * Le modèle de la documentation — une page d’aide est une DONNÉE, pas un écran.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI DES DONNÉES ET NON DES COMPOSANTS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Trente-sept pages écrites chacune en JSX, ce sont trente-sept mises en page à
 * relire, et surtout trente-sept occasions d’oublier une section. Le contrat du
 * chantier (PLAN-DOCUMENTATION §6) exige QUATRE choses de chaque page :
 *
 *   1. à quoi ça sert, en une phrase de métier ;
 *   2. le geste courant, avec les valeurs du décor de démonstration ;
 *   3. ★ ce qui peut mal se passer — la section qui sauve quelqu’un un soir de
 *      première ;
 *   4. ce que l’écran ne fait pas, quand la question se pose naturellement.
 *
 * Un type les rend obligatoires (1, 2, 3) ou explicitement facultatives (4), et
 * le garde-fou de couverture (`couverture.test.ts`) compte ce qui est vide. Une
 * page bâclée ne se cache pas dans un composant : elle apparaît comme un
 * tableau vide dans une structure de données.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * L’ANCRE — le seul identifiant que les écrans auront le droit de citer
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `aide/campagne/generer-propositions`. Trois segments, en minuscules, sans
 * accent : la racine `aide`, un DOMAINE (le sujet, pas le menu) et une RUBRIQUE.
 *
 * L’ancre ne dépend ni de la position de la page dans le sommaire, ni du public
 * auquel elle s’adresse — les deux bougeront, l’ancre non. C’est la condition
 * pour qu’un `<LienAide sujet="…" />` posé dans un écran d’administration ne
 * casse pas silencieusement le jour où la documentation se réorganise
 * (RESTE-A-FAIRE §L, « un schéma d’ancres stable »).
 *
 * L’URL s’en déduit mécaniquement : `/` + ancre. Il n’y a donc aucune table de
 * correspondance à maintenir, et donc aucune table à désynchroniser.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ★★ CE QUE LE MODÈLE A GAGNÉ — ET POURQUOI TOUT LE NEUF EST FACULTATIF
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Les quatre sections ci-dessus font un AIDE-MÉMOIRE : à quoi ça sert, le
 * geste, ce qui coince, ce que ça ne fait pas. Un aide-mémoire se lit debout,
 * et c’est précieux. Mais le retour du client demandait autre chose :
 *
 *   « chaque fonctionnalité EXPLIQUÉE ET APPROFONDIE … ILLUSTRÉE à l’aide
 *     d’exemples fictifs … AVEC DES CAPTURES D’ÉCRAN »
 *
 * Une phrase de résumé n’approfondit rien, et un tableau d’étapes n’illustre
 * rien. Il manquait quatre choses, et elles arrivent ici :
 *
 *  1. **des SECTIONS** (`sections`) — une page se découpe en blocs titrés, et
 *     CHAQUE titre porte un fragment stable. C’est la condition pour qu’un
 *     écran cite `aide/guichet/vendre#derogation` et tombe sur le paragraphe,
 *     pas sur le haut d’une page de six écrans de long ;
 *  2. **de la PROSE, des CAPTURES, des CAS CONCRETS, des ASTUCES et des
 *     AVERTISSEMENTS** (`BlocAide`) — la matière d’une vraie documentation,
 *     alternée à l’intérieur d’une section ;
 *  3. **les RÔLES** (`roles`, `SectionAide.role`) — « à destination des
 *     employés / décideurs » : qui est concerné doit se voir d’un coup d’œil,
 *     sur la page ET sur chaque section ;
 *  4. **le CHAPEAU** (`chapeau`) — le paragraphe d’ouverture qui pose le sujet,
 *     là où `resume` ne tient qu’une phrase (et sert d’entrée de sommaire).
 *
 * ★★ **TOUT LE NEUF EST FACULTATIF, ET CE N’EST PAS DE LA TIÉDEUR.** Trente-neuf
 * pages existent ; quatre lots vont les enrichir EN PARALLÈLE. Rendre `sections`
 * obligatoire ferait échouer la compilation des trente-huit pages qu’un lot n’a
 * pas encore touchées — c’est-à-dire bloquer les trois autres lots le temps
 * qu’un seul finisse. Le TYPE reste donc permissif ; c’est le garde-fou
 * (`couverture.test.tsx`) qui compte ce qui manque et qui refuse le bâclé.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * LE BALISAGE EN LIGNE — trois signes, et pas un de plus
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Le contenu reste de la DONNÉE : des chaînes dans des fichiers `.ts`, pas du
 * JSX. C’est ce qui permet au garde-fou de compter les pièges, de traquer
 * l’apostrophe droite et de refuser un lien mort — trois choses qu’on ne sait
 * pas faire sur un arbre de composants. Mais une documentation sans un mot en
 * gras ni un renvoi cliquable est un mur de texte.
 *
 * D’où trois signes, reconnus par `elements.tsx` dans tout texte de bloc :
 *
 *   `**gras**`                      ce qu’il faut retenir dans la phrase
 *   `` `code` ``                    un libellé d’écran, une valeur, un champ
 *   `[libellé](aide/x/y#fragment)`  un renvoi vers une autre page — VÉRIFIÉ
 *
 * Rien d’autre. Pas de HTML, jamais de `dangerouslySetInnerHTML` : le texte est
 * découpé en fragments et rendu par React, donc rien de ce qu’écrit un lot de
 * contenu ne peut injecter de balise.
 */

/* -------------------------------------------------------------------------- */
/* Le contenu d’une page                                                      */
/* -------------------------------------------------------------------------- */

/** Une étape du geste courant. Le `detail` porte la valeur du décor. */
export interface EtapeAide {
  readonly geste: string
  readonly detail?: string
}

/**
 * ★ Un piège — la section 3, celle qui compte.
 *
 * `situation` est écrite du point de vue de qui la subit (« le siège que je
 * viens de choisir est refusé »), jamais du point de vue du code. `reponse` dit
 * ce qui se passe ET ce qu’il faut faire ; une explication sans geste ne sert à
 * personne à 19 h 55.
 */
export interface PiegeAide {
  readonly situation: string
  readonly reponse: string
}

/* -------------------------------------------------------------------------- */
/* Les rôles — « qui est concerné », visible d’un coup d’œil                   */
/* -------------------------------------------------------------------------- */

/**
 * Qui la page (ou la section) concerne.
 *
 * ★ Les trois premiers sont EXACTEMENT les rôles du produit
 * (`RoleUtilisateur` de `@tb/shared`) : une documentation qui inventerait ses
 * propres catégories de personnel ferait chercher au lecteur un rôle qu’aucun
 * écran de comptes ne propose. Les deux derniers ne sont pas des rôles de
 * connexion — ils n’en ont pas — mais ce sont bien deux publics du contrat
 * (PLAN-DOCUMENTATION §3), et un badge sur une page publique évite qu’une
 * administratrice y cherche un réglage.
 *
 * ★ Union FERMÉE, et c’est délibéré : une faute de frappe dans un lot de
 * contenu doit échouer à la compilation, pas rendre un badge vide.
 */
export type RoleAide = 'admin' | 'guichet' | 'lecture' | 'controle' | 'spectateur' | 'organisateur' | 'partenaire'

/* -------------------------------------------------------------------------- */
/* Les blocs — la matière d’une section                                       */
/* -------------------------------------------------------------------------- */

/** Une ligne de repère : un terme du produit, et ce qu’il vaut ou signifie. */
export interface RepereAide {
  readonly terme: string
  readonly valeur: string
}

/**
 * Un bloc de contenu, à l’intérieur d’une section.
 *
 * L’union est discriminée par `type` : ajouter un bloc, c’est ajouter un
 * membre ici ET une branche dans `elements.tsx`, et TypeScript refuse d’oublier
 * la seconde (`switch` exhaustif). Un bloc muet ne peut donc pas exister.
 *
 * Les textes acceptent le balisage en ligne décrit en tête de fichier.
 */
export type BlocAide =
  /** Un paragraphe. C’est ce qui manquait le plus : sans prose, rien n’est approfondi. */
  | { readonly type: 'prose'; readonly texte: string }
  /** Une énumération à puces — des cas, des variantes, des conditions. */
  | { readonly type: 'liste'; readonly items: readonly string[] }
  /** Des étapes numérotées, comme le geste courant mais à l’échelle d’une section. */
  | { readonly type: 'etapes'; readonly etapes: readonly EtapeAide[] }
  /**
   * ★ Une capture d’écran.
   *
   * `capture` désigne une entrée du CATALOGUE (`captures.ts`) — jamais un
   * chemin de fichier écrit à la main, qui pourrit sans que personne le voie.
   *
   * `alt` DÉCRIT l’image pour qui ne la voit pas (« le plan de salle du Théâtre
   * du Bourg-Neuf, zones colorées, sièges vendus en gris »). `legende` APPREND
   * quelque chose de plus que l’image ; « capture de l’écran des ventes » n’est
   * ni l’un ni l’autre, et le garde-fou le refuse.
   */
  | {
      readonly type: 'capture'
      readonly capture: string
      readonly alt: string
      readonly legende: string
    }
  /**
   * ★ Un cas concret — l’exemple fictif que le client réclame nommément.
   *
   * Le décor impose les noms (DECOR-DEMONSTRATION §5) : Anne Zufferey, Luc
   * Fournier, les Dubuis, Sophie Rey, le Théâtre du Bourg-Neuf. Un exemple qui
   * invente son propre décor contredit la capture posée juste au-dessus.
   */
  | {
      readonly type: 'cas'
      readonly titre: string
      /** Le contexte en UNE phrase : qui, quand, avec quoi devant les yeux. */
      readonly contexte: string
      readonly etapes: readonly string[]
    }
  /** Un conseil. Ton vert : on peut ne pas le suivre, il ne coûte rien. */
  | { readonly type: 'astuce'; readonly texte: string }
  /** Un danger. Ton ambre : l’ignorer coûte quelque chose, et on doit le voir. */
  | { readonly type: 'avertissement'; readonly texte: string }
  /** Une table de repères : statuts, champs, valeurs du décor. */
  | { readonly type: 'reperes'; readonly lignes: readonly RepereAide[] }

/* -------------------------------------------------------------------------- */
/* Les sections — ce qui rend une page citable au paragraphe près             */
/* -------------------------------------------------------------------------- */

/**
 * Un bloc titré d’une page, porteur d’un FRAGMENT stable.
 *
 * ★ C’est la pièce qui manquait. Aujourd’hui un écran cite une PAGE ; demain il
 * cite `aide/guichet/vendre#derogation` et le lecteur tombe sur le paragraphe
 * qui le concerne. La différence n’est pas cosmétique : sur une page de six
 * écrans de long, « le haut de la page » et « la réponse » ne sont pas au même
 * endroit, et quelqu’un qui a une file d’attente derrière lui ne défile pas.
 *
 * `id` obéit à `FORME_FRAGMENT` et ne doit pas heurter les fragments que la
 * mise en page pose elle-même (`FRAGMENTS_RESERVES`) : le garde-fou vérifie les
 * deux, parce qu’un doublon d’identifiant rend le lien profond aléatoire.
 */
export interface SectionAide {
  readonly id: string
  readonly titre: string
  /** Qui est concerné par CETTE section — souvent plus étroit que la page. */
  readonly role?: RoleAide
  readonly blocs: readonly BlocAide[]
}

export interface PageAide {
  /** `aide/<domaine>/<rubrique>` — stable, citée par les écrans. */
  readonly ancre: string
  readonly titre: string
  /** Section 1 : à quoi ça sert, une phrase, du point de vue du métier. */
  readonly resume: string
  /**
   * Où la fonction se trouve dans le produit, en toutes lettres
   * (« Campagne → Générer les propositions »).
   *
   * ★ C’est un LIBELLÉ, pas un lien. La documentation est servie aussi bien
   * depuis l’adresse d’un théâtre que depuis l’apex, où `/admin` ne mène nulle
   * part : un lien y serait mort. Un chemin de menu écrit se lit partout.
   */
  readonly ou: string
  /** Section 2 : le geste courant, avec les valeurs du décor. */
  readonly geste: readonly EtapeAide[]
  /** Section 3 : ce qui peut mal se passer. Au moins un. */
  readonly pieges: readonly PiegeAide[]
  /**
   * Section 4 : ce que l’écran ne fait pas. Facultative — « quand la question
   * se pose naturellement ». Une limite inventée pour remplir la section vaut
   * moins que rien : elle apprend au lecteur à sauter la rubrique.
   */
  readonly limites?: readonly string[]
  /**
   * Références vers d’autres pages, fragment compris
   * (`aide/paiements/encaisser#relancer`). Chacune est vérifiée par
   * `couverture.test.tsx` — page ET fragment.
   */
  readonly voirAussi?: readonly string[]

  /* ───────────────────────── le neuf, tout facultatif ───────────────────── */

  /**
   * Le paragraphe d’ouverture, sous le titre. `resume` tient en une phrase
   * parce qu’il sert aussi d’entrée de sommaire ; le chapeau, lui, a le droit
   * de poser le sujet, de nommer les cas et d’annoncer le plan.
   */
  readonly chapeau?: string
  /** Qui la page concerne. Affiché en badges sous le titre. */
  readonly roles?: readonly RoleAide[]
  /**
   * ★ Le corps approfondi : des blocs titrés, chacun citable par son fragment.
   *
   * Ordre de lecture rendu par `PageRubrique` : le geste courant d’abord (la
   * réponse rapide), les sections ensuite (le fond), les pièges après (le
   * dépannage), les limites pour finir. Une page riche reste donc consultable
   * en dix secondes par quelqu’un qui sait déjà ce qu’il cherche.
   */
  readonly sections?: readonly SectionAide[]
}

/* -------------------------------------------------------------------------- */
/* Les publics                                                                */
/* -------------------------------------------------------------------------- */

/**
 * ★ Le sommaire est organisé par MÉTIER, pas par menu (PLAN-DOCUMENTATION §3).
 *
 * Quelqu’un cherche « comment j’encaisse un paiement », pas « chapitre 7.3 ».
 * Une même page peut donc figurer chez plusieurs publics — la QR-facture
 * concerne le guichet qui l’émet et le spectateur qui la reçoit — sans être
 * écrite deux fois : c’est l’ANCRE qui est partagée, pas le texte dupliqué.
 */
export interface PublicAide {
  readonly id: string
  readonly titre: string
  /** La personne du décor de démonstration qui incarne ce métier. */
  readonly personne: string
  readonly resume: string
  readonly ancres: readonly string[]
}

/* -------------------------------------------------------------------------- */
/* Les chapitres — la colonne vertébrale de la coque                          */
/* -------------------------------------------------------------------------- */

/**
 * ★★ UN CHAPITRE, C’EST UN MOMENT DU MÉTIER — et c’est la SECONDE vue.
 *
 * Les `PublicAide` classent par MÉTIER : une même page y figure chez plusieurs
 * publics (la facture QR concerne le guichet qui l’émet et le spectateur qui la
 * reçoit), et c’est ce qui fait un bon sommaire. Mais une barre latérale
 * permanente ne peut pas répéter la même page trois fois : elle doit dire OÙ
 * L’ON EST, et « on est à trois endroits » n’est pas une réponse.
 *
 * D’où une seconde vue, LINÉAIRE, où chaque page apparaît EXACTEMENT UNE FOIS,
 * dans l’ordre où on écrirait le manuel : démarrer, monter la saison, mener la
 * campagne, tenir le guichet, tenir la porte, côté public, piloter, un
 * événement ponctuel. C’est elle qui porte le fil d’Ariane, la barre latérale
 * et le « rubrique précédente / suivante ».
 *
 * ★ **Un chapitre possède des DOMAINES, pas des pages.** Lister les pages
 * obligerait chacun des quatre lots de contenu à revenir modifier ce fichier —
 * donc quatre lots dans le même fichier, donc des conflits. En listant les
 * domaines (le deuxième segment de l’ancre), une page ajoutée à un domaine
 * connu se range toute seule, et seul un domaine ENTIÈREMENT nouveau exige une
 * ligne ici. Le garde-fou le dit alors avec un message qui nomme le domaine
 * orphelin, au lieu de laisser la page disparaître de la barre latérale.
 */
export interface ChapitreAide {
  readonly id: string
  readonly titre: string
  /** Une phrase, affichée en tête de chapitre dans le sommaire. */
  readonly resume: string
  /** Les domaines qui lui appartiennent — deuxième segment de l’ancre. */
  readonly domaines: readonly string[]
}

/** `aide/guichet/vendre` → `guichet`. Chaîne vide si l’ancre est mal formée. */
export function domaineDe(ancre: string): string {
  return ancre.split('/')[1] ?? ''
}

/* -------------------------------------------------------------------------- */
/* Adressage                                                                  */
/* -------------------------------------------------------------------------- */

/** La racine de la documentation. Sans garde de session, comme la boutique. */
export const CHEMIN_AIDE = '/aide'

/**
 * `aide/campagne/phases` → `/aide/campagne/phases`.
 *
 * ★ Le fragment passe SANS TRAITEMENT PARTICULIER, et c’est tout l’intérêt
 * d’avoir gardé la concaténation nue : `aide/guichet/vendre#derogation` donne
 * `/aide/guichet/vendre#derogation`. Rien à ajouter, rien à désynchroniser.
 */
export function urlAide(reference: string): string {
  return `/${reference}`
}

/** `/aide/campagne/phases` → `aide/campagne/phases`. */
export function ancreDepuisUrl(domaine: string, rubrique: string): string {
  return `aide/${domaine}/${rubrique}`
}

/**
 * La forme exigée d’une ancre : trois segments, minuscules, chiffres et tirets.
 * Exportée parce que le garde-fou de couverture la vérifie sur chaque page —
 * une ancre accentuée ou capitalisée voyagerait mal dans une URL et se
 * réécrirait un jour « proprement », cassant tous les liens qui la citent.
 */
export const FORME_ANCRE = /^aide\/[a-z0-9-]+\/[a-z0-9-]+$/u

/**
 * La forme d’un FRAGMENT de section : minuscules, chiffres, tirets.
 *
 * Mêmes contraintes que l’ancre et pour la même raison — un fragment accentué
 * voyage mal dans une URL, se réécrit un jour « proprement », et emporte avec
 * lui tous les liens profonds qui le citaient.
 */
export const FORME_FRAGMENT = /^[a-z0-9-]+$/u

/**
 * La forme d’une RÉFÉRENCE : une ancre, éventuellement suivie d’un fragment.
 *
 * ★★ `FORME_ANCRE` N’A PAS BOUGÉ, ET NE DOIT PAS BOUGER. Elle qualifie
 * l’identité d’une PAGE, celle que `contenu/` déclare et que
 * `navigation-aide.test.ts` vérifie sur chaque entrée de menu. Une référence,
 * elle, est ce qu’on ÉCRIT quelque part pour désigner un endroit — un
 * `voirAussi`, un `[libellé](…)` dans un texte, et demain le `LienAide` d’un
 * écran qui veut tomber sur le bon paragraphe. Les confondre reviendrait à
 * autoriser une page à s’appeler `…#quelque-chose`.
 */
export const FORME_REFERENCE = /^aide\/[a-z0-9-]+\/[a-z0-9-]+(?:#[a-z0-9-]+)?$/u

/**
 * Les fragments que la MISE EN PAGE pose elle-même, et qu’une section déclarée
 * dans `contenu/` ne peut donc pas reprendre.
 *
 * Deux éléments du même `id` dans un document, c’est un lien profond qui tombe
 * tantôt sur l’un tantôt sur l’autre selon le navigateur. Le garde-fou refuse
 * la collision plutôt que de la laisser se découvrir en production.
 */
export const FRAGMENTS_RESERVES = [
  'contenu',
  'sur-cette-page',
  'geste',
  'pieges',
  'limites',
  'a-lire-ensuite',
] as const

/** Une référence découpée en ce qu’elle désigne : une page, et un endroit dedans. */
export interface ReferenceAide {
  readonly ancre: string
  readonly fragment?: string
}

/**
 * `aide/guichet/vendre#derogation` → `{ ancre: 'aide/guichet/vendre',
 * fragment: 'derogation' }`.
 *
 * Sans fragment, `fragment` est absent — pas une chaîne vide : `undefined` se
 * teste, `''` se compare par distraction à un identifiant vrai.
 */
export function decouperReference(reference: string): ReferenceAide {
  const separateur = reference.indexOf('#')
  if (separateur === -1) return { ancre: reference }
  return {
    ancre: reference.slice(0, separateur),
    fragment: reference.slice(separateur + 1),
  }
}
